/**
 * Turning Bible clauses into the composed prompt, within the model's character budget.
 *
 * Split out of `resolver.js`, which was well past its size warning, and rewritten from a
 * tail slice to whole-clause selection.
 *
 * THE DEFECT THIS REPLACES, measured against a realistically rich Bible: the composed prompt
 * reached 519 characters against a 520 budget and was cut to
 * `"... standing water visible somewhere in..."`. Two things were wrong with that, and the
 * ugliness was the lesser one. The clause severed mid-word was a `rules.must` — a hard rule —
 * and the `never` rule after it was dropped entirely. A slice at a character offset has no
 * idea what it is cutting, and rules compose last, so the rules were always what it ate.
 *
 * So clauses are now selected WHOLE, rules first. A clause either makes it or it does not,
 * and one that does not is reported rather than half-delivered.
 *
 * On not reusing `promptAdapters/promptAdapterText.js`'s `applyPromptBudgetFromBuckets`:
 * it already does required/optional selection, and the selection idea here is deliberately
 * the same. What it does NOT share is the rendering — it normalizes each segment as a
 * sentence and joins on spaces, which is Ghost Whisper's prompt format. Adopting it whole
 * would silently rewrite every Bible clause and change the composed text, which is a far
 * bigger change than the budget fix it was meant to buy. Same algorithm, different renderer.
 */

export function promptCharBudgetFor(promptBudget = 'medium', prefersConcisePrompt = false) {
  const maxChars = promptBudget === 'short'
    ? 280
    : promptBudget === 'long'
      ? 900
      : 520;
  return prefersConcisePrompt ? Math.min(maxChars, 360) : maxChars;
}

function compactList(values = []) {
  return values
    .filter((value) => typeof value === 'string' && value.trim())
    .map((value) => value.trim());
}

function renderPrompt(basePrompt, clauses) {
  if (clauses.length === 0) return basePrompt;
  // Clauses arrive from user-authored fields, and a thesis very often already ends in a full
  // stop -- which produced "the tide never fully leaves.." in every prompt carrying one. Strip
  // trailing sentence punctuation before joining so the separator is the ONLY punctuation this
  // step adds. Cosmetic, but it is in front of the model on every single generation.
  const tidy = clauses.map((clause) => String(clause).replace(/[.;,\s]+$/, '')).filter(Boolean);
  const suffix = `Creative direction: ${tidy.join('. ')}.`;
  return basePrompt ? `${basePrompt}\n\n${suffix}` : suffix;
}

/**
 * `required` clauses are admitted before any optional one and are only dropped when they do
 * not fit even on their own terms — rules are the user's hard constraints, and losing them to
 * make room for a texture note is exactly backwards.
 *
 * The user's own prompt is never trimmed. It was not trimmed before either: the old path
 * returned it untouched whenever there were no additions, so this keeps the guarantee and
 * extends it rather than inventing one.
 */
export function composeBiblePrompt({
  basePrompt = '',
  requiredClauses = [],
  optionalClauses = [],
  capabilityProfile = null,
} = {}) {
  const normalizedBase = (basePrompt || '').trim();
  const required = compactList(requiredClauses);
  const optional = compactList(optionalClauses);

  if (required.length === 0 && optional.length === 0) {
    return { prompt: normalizedBase, omittedClauses: [] };
  }

  const budget = promptCharBudgetFor(
    capabilityProfile?.promptBudget,
    capabilityProfile?.prefersConcisePrompt,
  );

  const selected = [];
  const omittedClauses = [];
  const seen = new Set();

  const admit = (clause) => {
    const key = clause.toLowerCase();
    if (seen.has(key)) return;
    const candidate = renderPrompt(normalizedBase, [...selected, clause]);
    // A clause that would push the whole prompt past budget is left out WHOLE. The first
    // clause is admitted regardless, so a Bible always contributes something rather than
    // silently contributing nothing on a long user prompt.
    if (candidate.length > budget && selected.length > 0) {
      omittedClauses.push({ clause, reason: 'budget' });
      return;
    }
    seen.add(key);
    selected.push(clause);
  };

  required.forEach(admit);
  optional.forEach(admit);

  return { prompt: renderPrompt(normalizedBase, selected), omittedClauses };
}

export default composeBiblePrompt;
